import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import "./index.css";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import api from "./lib/api";

import Header from "./components/site/Header";
import Footer from "./components/site/Footer";

import Home from "./pages/site/Home";
import Shop from "./pages/site/Shop";
import Product from "./pages/site/Product";
import Legacy from "./pages/site/Legacy";
import OurStory from "./pages/site/OurStory";
import FitGuide from "./pages/site/FitGuide";
import Journal from "./pages/site/Journal";
import JournalPost from "./pages/site/JournalPost";
import Contact from "./pages/site/Contact";
import Cart from "./pages/site/Cart";
import Checkout from "./pages/site/Checkout";
import Login from "./pages/site/Login";
import Register from "./pages/site/Register";
import Account from "./pages/site/Account";

import AppShell from "./pages/app/AppShell";
import AppHome from "./pages/app/AppHome";
import AppShop from "./pages/app/AppShop";
import AppProduct from "./pages/app/AppProduct";
import AppTryOn from "./pages/app/AppTryOn";
import AppFitProfile from "./pages/app/AppFitProfile";
import AppWishlist from "./pages/app/AppWishlist";
import AppCart from "./pages/app/AppCart";
import AppLoyalty from "./pages/app/AppLoyalty";
import AppAuthenticity from "./pages/app/AppAuthenticity";
import AppConcierge from "./pages/app/AppConcierge";
import AppProfile from "./pages/app/AppProfile";

import AdminShell from "./pages/admin/AdminShell";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminLegacy from "./pages/admin/AdminLegacy";
import AdminAuthenticity from "./pages/admin/AdminAuthenticity";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminConcierge from "./pages/admin/AdminConcierge";

const SiteLayout = () => (
  <div className="min-h-screen flex flex-col bg-poche-black text-white">
    <Header />
    <main className="flex-1">
      <Outlet />
    </main>
    <Footer />
  </div>
);

const RequireAdmin = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/50 text-sm tracking-luxury uppercase">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
};

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      const hash = location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      if (!m) {
        navigate("/login");
        return;
      }
      const sessionId = m[1];
      try {
        const res = await api.post("/auth/google/session", { session_id: sessionId });
        // Also store session_token as our JWT-like token to also support header auth
        if (res.data.session_token) {
          localStorage.setItem("poche_token", res.data.session_token);
        }
        navigate("/account", { replace: true, state: { user: res.data.user } });
      } catch (e) {
        setError("Authentication failed. Please try again.");
        setTimeout(() => navigate("/login"), 2000);
      }
    };
    run();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-poche-black text-white/60 text-sm tracking-luxury uppercase">
      {error || "Completing sign in…"}
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Website */}
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:id" element={<Product />} />
        <Route path="/legacy" element={<Legacy />} />
        <Route path="/our-story" element={<OurStory />} />
        <Route path="/fit-guide" element={<FitGuide />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/journal/:slug" element={<JournalPost />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/account" element={<Account />} />
      </Route>

      {/* Mobile App preview */}
      <Route path="/app" element={<AppShell />}>
        <Route index element={<AppHome />} />
        <Route path="shop" element={<AppShop />} />
        <Route path="product/:id" element={<AppProduct />} />
        <Route path="try-on" element={<AppTryOn />} />
        <Route path="fit-profile" element={<AppFitProfile />} />
        <Route path="wishlist" element={<AppWishlist />} />
        <Route path="cart" element={<AppCart />} />
        <Route path="loyalty" element={<AppLoyalty />} />
        <Route path="authenticity" element={<AppAuthenticity />} />
        <Route path="concierge" element={<AppConcierge />} />
        <Route path="profile" element={<AppProfile />} />
      </Route>

      {/* Admin Portal */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminShell />
          </RequireAdmin>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="legacy" element={<AdminLegacy />} />
        <Route path="authenticity" element={<AdminAuthenticity />} />
        <Route path="concierge" element={<AdminConcierge />} />
        <Route path="analytics" element={<AdminAnalytics />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRouter />
          <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: "#121212", color: "#fff", border: "1px solid rgba(201,168,100,0.3)", borderRadius: 0 } }} />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
